// Servicio de gestión de notas detalladas

import { get, post, put, del } from './api'
import { PAGINATION_CONFIG } from '../utils/constants'

export const detalleNotaService = {
  /**
   * Obtener lista de notas con paginación
   * @param {object} params - Parámetros de consulta
   * @returns {Promise<object>} Lista paginada de notas
   */
  async getNotas(params = {}) {
    try {
      const queryParams = {
        page: params.page || 1,
        per_page: params.per_page || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
        estudiante_id: params.estudiante_id || '',
        evaluacion_id: params.evaluacion_id || '',
        periodo_id: params.periodo_id || ''
      }

      const response = await get('/admin/notas-detalle', queryParams)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al obtener notas',
          data: null
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        data: null
      }
    }
  },

  /**
   * Obtener notas de un estudiante por período
   * @param {number} estudianteId - ID del estudiante
   * @param {number} periodoId - ID del período
   * @returns {Promise<object>} Notas del estudiante
   */
  async getNotasEstudiante(estudianteId, periodoId) {
    try {
      const response = await get(`/admin/notas-detalle/estudiante/${estudianteId}/${periodoId}`)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || {},
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al obtener notas del estudiante',
          data: {}
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        data: {}
      }
    }
  },

  /**
   * Obtener notas de una evaluación (todos los estudiantes)
   * @param {number} evaluacionId - ID de la evaluación
   * @returns {Promise<object>} Notas de la evaluación
   */
  async getNotasEvaluacion(evaluacionId) {
    try {
      const response = await get(`/admin/notas-detalle/evaluacion/${evaluacionId}`)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al obtener notas de la evaluación',
          data: []
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        data: []
      }
    }
  },

  /**
   * Crear nota individual
   * @param {object} data - Datos de la nota
   * @returns {Promise<object>} Nota creada
   */
  async createNota(data) {
    try {
      const response = await post('/admin/notas-detalle', data)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al crear nota',
          errors: response.data?.errors || null,
          data: null
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        errors: error.response?.data?.errors || null,
        data: null
      }
    }
  },

  /**
   * Cargar notas de forma masiva
   * @param {object} data - Datos de notas { evaluacion_id, notas: [...] }
   * @returns {Promise<object>} Resultado de la carga masiva
   */
  async createNotasMasivo(data) {
    try {
      const response = await post('/admin/notas-detalle/masivo', data)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al cargar notas',
          errors: response.data?.errors || null,
          data: null
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        errors: error.response?.data?.errors || null,
        data: null
      }
    }
  },

  /**
   * Obtener nota por ID
   * @param {number} id - ID de la nota
   * @returns {Promise<object>} Nota solicitada
   */
  async getNota(id) {
    try {
      const response = await get(`/admin/notas-detalle/${id}`)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Nota no encontrada',
          data: null
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        data: null
      }
    }
  },

  /**
   * Actualizar nota
   * @param {number} id - ID de la nota
   * @param {object} data - Datos a actualizar
   * @returns {Promise<object>} Nota actualizada
   */
  async updateNota(id, data) {
    try {
      const response = await put(`/admin/notas-detalle/${id}`, data)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al actualizar nota',
          errors: response.data?.errors || null,
          data: null
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión',
        errors: error.response?.data?.errors || null,
        data: null
      }
    }
  },

  /**
   * Eliminar nota
   * @param {number} id - ID de la nota
   * @returns {Promise<object>} Resultado de la eliminación
   */
  async deleteNota(id) {
    try {
      const response = await del(`/admin/notas-detalle/${id}`)
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message
        }
      } else {
        return {
          success: false,
          message: response.data?.message || 'Error al eliminar nota'
        }
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error de conexión'
      }
    }
  }
}
